import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  DollarSign,
  CheckCircle2,
  Clock,
  Download,
  Filter,
  User,
  Calendar,
  CreditCard,
  Loader2,
  RefreshCw,
  CheckCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FinanceiroInscricoesPage() {
  const { token } = useAuth();
  const [inscricoes, setInscricoes] = useState([]);
  const [projetosPagos, setProjetosPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroProjetoId, setFiltroProjetoId] = useState("todos");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [selecionados, setSelecionados] = useState([]);
  const [baixandoLote, setBaixandoLote] = useState(false);

  useEffect(() => {
    loadData();
  }, [filtroProjetoId, filtroStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar projetos pagos para o filtro
      const projetosRes = await axios.get(`${API}/financeiro/projetos-pagos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjetosPagos(projetosRes.data || []);
      
      // Carregar inscrições
      let url = `${API}/financeiro/inscricoes`;
      const params = new URLSearchParams();
      if (filtroProjetoId && filtroProjetoId !== "todos") {
        params.append("projeto_id", filtroProjetoId);
      }
      if (filtroStatus && filtroStatus !== "todos") {
        params.append("status_pagamento", filtroStatus);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const inscricoesRes = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInscricoes(inscricoesRes.data || []);
      setSelecionados([]);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados do financeiro");
    } finally {
      setLoading(false);
    }
  };

  const handleBaixarPagamento = async (projetoId, pessoaId) => {
    try {
      await axios.put(
        `${API}/financeiro/baixar-pagamento/${projetoId}/${pessoaId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Pagamento baixado com sucesso!");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao baixar pagamento");
    }
  };

  const handleBaixarLote = async () => {
    if (selecionados.length === 0) {
      toast.error("Selecione pelo menos um pagamento");
      return;
    }

    setBaixandoLote(true);
    try {
      const pagamentos = selecionados.map(key => {
        const [projetoId, pessoaId] = key.split("_");
        return { projeto_id: projetoId, pessoa_id: pessoaId };
      });

      const res = await axios.post(
        `${API}/financeiro/baixar-pagamentos-lote`,
        { pagamentos },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao baixar pagamentos");
    } finally {
      setBaixandoLote(false);
    }
  };

  const toggleSelecao = (projetoId, pessoaId) => {
    const key = `${projetoId}_${pessoaId}`;
    if (selecionados.includes(key)) {
      setSelecionados(selecionados.filter(k => k !== key));
    } else {
      setSelecionados([...selecionados, key]);
    }
  };

  const toggleSelecionarTodosConfirmados = () => {
    const confirmados = inscricoes
      .filter(i => i.status_pagamento === "confirmado")
      .map(i => `${i.projeto_id}_${i.pessoa_id}`);
    
    if (confirmados.every(key => selecionados.includes(key))) {
      // Desselecionar todos
      setSelecionados(selecionados.filter(k => !confirmados.includes(k)));
    } else {
      // Selecionar todos confirmados
      const novosSelecionados = [...new Set([...selecionados, ...confirmados])];
      setSelecionados(novosSelecionados);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pendente":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200"><Clock className="h-3 w-3 mr-1" />Pendente</Badge>;
      case "confirmado":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><CheckCircle2 className="h-3 w-3 mr-1" />Confirmado</Badge>;
      case "baixado":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCheck className="h-3 w-3 mr-1" />Baixado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "R$ 0,00";
    return `R$ ${value.toFixed(2).replace(".", ",")}`;
  };

  // Estatísticas
  const totalInscricoes = inscricoes.length;
  const totalPendentes = inscricoes.filter(i => i.status_pagamento === "pendente").length;
  const totalConfirmados = inscricoes.filter(i => i.status_pagamento === "confirmado").length;
  const totalBaixados = inscricoes.filter(i => i.status_pagamento === "baixado").length;
  const valorTotal = inscricoes.reduce((acc, i) => acc + (i.valor_inscricao || 0), 0);
  const valorConfirmado = inscricoes.filter(i => i.status_pagamento === "confirmado" || i.status_pagamento === "baixado")
    .reduce((acc, i) => acc + (i.valor_inscricao || 0), 0);

  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="h-7 w-7 text-green-600" />
            Financeiro - Inscrições
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerencie os pagamentos das inscrições em eventos pagos
          </p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{totalInscricoes}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                <User className="h-5 w-5 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pendentes</p>
                <p className="text-2xl font-bold text-yellow-600">{totalPendentes}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Confirmados</p>
                <p className="text-2xl font-bold text-blue-600">{totalConfirmados}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Baixados</p>
                <p className="text-2xl font-bold text-green-600">{totalBaixados}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCheck className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Card de Valores */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Valor Total Esperado</p>
                <p className="text-3xl font-bold text-green-800">{formatCurrency(valorTotal)}</p>
              </div>
              <DollarSign className="h-10 w-10 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">Valor Confirmado/Baixado</p>
                <p className="text-3xl font-bold text-blue-800">{formatCurrency(valorConfirmado)}</p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Filtrar por Evento</label>
              <Select value={filtroProjetoId} onValueChange={setFiltroProjetoId}>
                <SelectTrigger data-testid="filtro-projeto">
                  <SelectValue placeholder="Todos os eventos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os eventos</SelectItem>
                  {projetosPagos.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nome} - {formatCurrency(p.valor_inscricao)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Filtrar por Status</label>
              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger data-testid="filtro-status">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="confirmado">Confirmado</SelectItem>
                  <SelectItem value="baixado">Baixado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {totalConfirmados > 0 && (
              <Button 
                onClick={handleBaixarLote}
                disabled={baixandoLote || selecionados.length === 0}
                className="bg-green-600 hover:bg-green-700"
                data-testid="baixar-lote-btn"
              >
                {baixandoLote ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Baixar Selecionados ({selecionados.length})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lista de Inscrições */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Inscrições</CardTitle>
            {totalConfirmados > 0 && (
              <Button variant="ghost" size="sm" onClick={toggleSelecionarTodosConfirmados}>
                <Checkbox 
                  checked={inscricoes.filter(i => i.status_pagamento === "confirmado").every(i => 
                    selecionados.includes(`${i.projeto_id}_${i.pessoa_id}`)
                  )}
                  className="mr-2"
                />
                Selecionar todos confirmados
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : inscricoes.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>Nenhuma inscrição encontrada</p>
              <p className="text-sm">Ajuste os filtros ou aguarde novas inscrições</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium text-sm w-10"></th>
                    <th className="pb-3 font-medium text-sm">Participante</th>
                    <th className="pb-3 font-medium text-sm">Evento</th>
                    <th className="pb-3 font-medium text-sm">Valor</th>
                    <th className="pb-3 font-medium text-sm">Forma Pgto</th>
                    <th className="pb-3 font-medium text-sm">Data Inscrição</th>
                    <th className="pb-3 font-medium text-sm">Status</th>
                    <th className="pb-3 font-medium text-sm">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {inscricoes.map((inscricao) => {
                    const key = `${inscricao.projeto_id}_${inscricao.pessoa_id}`;
                    const isSelected = selecionados.includes(key);
                    
                    return (
                      <tr key={key} className="border-b last:border-0 hover:bg-muted/50">
                        <td className="py-3">
                          {inscricao.status_pagamento === "confirmado" && (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSelecao(inscricao.projeto_id, inscricao.pessoa_id)}
                            />
                          )}
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                            <span className="font-medium">{inscricao.pessoa_nome}</span>
                          </div>
                        </td>
                        <td className="py-3">
                          <span className="text-sm">{inscricao.projeto_nome}</span>
                        </td>
                        <td className="py-3">
                          <span className="font-semibold text-green-600">
                            {formatCurrency(inscricao.valor_inscricao)}
                          </span>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <CreditCard className="h-3 w-3" />
                            {inscricao.forma_pagamento || "-"}
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {formatDate(inscricao.data_inscricao)}
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="space-y-1">
                            {getStatusBadge(inscricao.status_pagamento)}
                            {inscricao.pagamento_confirmado_em && (
                              <p className="text-xs text-muted-foreground">
                                Conf: {inscricao.pagamento_confirmado_por_nome} em {formatDateTime(inscricao.pagamento_confirmado_em)}
                              </p>
                            )}
                            {inscricao.pagamento_baixado_em && (
                              <p className="text-xs text-muted-foreground">
                                Baixa: {inscricao.pagamento_baixado_por_nome} em {formatDateTime(inscricao.pagamento_baixado_em)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3">
                          {inscricao.status_pagamento === "confirmado" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => handleBaixarPagamento(inscricao.projeto_id, inscricao.pessoa_id)}
                              data-testid={`baixar-${key}`}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Baixar
                            </Button>
                          )}
                          {inscricao.status_pagamento === "baixado" && (
                            <span className="text-sm text-green-600 flex items-center gap-1">
                              <CheckCheck className="h-4 w-4" />
                              Concluído
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
